import { ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { buildTenantPath, normalizeTenantSlug } from '../../utils/tenantSlug';
import { isClientAccessActive } from '../../utils/clientAccess';

interface AuthGuardProps {
  children: ReactNode;
  allowedRoles?: string[];
  requireAuth?: boolean;
  requireClientSelected?: boolean; // New prop
}

const AuthGuard = ({
  children,
  allowedRoles,
  requireAuth = true,
  requireClientSelected = true // Default to true for protected routes
}: AuthGuardProps) => {
  const { user, isAuthenticated, isLoading, isClientSelected, selectedClient, getDashboardPath } = useAuth();
  const location = useLocation();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const isSuperAdmin = user?.userGlobalRole === "superAdmin";
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const routeSegments = tenantSlug ? pathSegments.slice(1) : pathSegments;
  const isClientSelectionRoute = routeSegments[0] === "selecionar-cliente";
  const hasActiveClientAccess = selectedClient
    ? isClientAccessActive(selectedClient.accessStartAt, selectedClient.accessEndAt)
    : true;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // If authentication is required and user is not authenticated, redirect to login
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={buildTenantPath("/login", tenantSlug)} state={{ from: location }} replace />;
  }

  // If authentication is not required and user is authenticated, redirect away from public pages
  if (!requireAuth && isAuthenticated) {
    // Redirect authenticated users from public pages (like login)
    // The AuthProvider will handle finding the correct dashboard or sending to client selection
    // We can use a generic authenticated path, or rely on the effect in AuthContext
    // For now, a simple redirect to a base path is fine, as Auth logic will take over.
    const redirectPath = isSuperAdmin
      ? getDashboardPath("superAdmin", tenantSlug)
      : selectedClient
        ? getDashboardPath(selectedClient.userRole, selectedClient.clientSlug || tenantSlug)
        : buildTenantPath("/selecionar-cliente", tenantSlug);
    return <Navigate to={redirectPath} replace />;
  }
  
  // If auth is required, but client is not selected, redirect to client selection page
  if (requireAuth && isAuthenticated && requireClientSelected && !isClientSelected) {
    if (isSuperAdmin) {
      return <Navigate to={getDashboardPath("superAdmin", tenantSlug)} replace />;
    }
    return <Navigate to={buildTenantPath("/selecionar-cliente", tenantSlug)} state={{ from: location }} replace />;
  }

  if (requireAuth && requireClientSelected && selectedClient && !hasActiveClientAccess) {
    return <Navigate to={buildTenantPath("/acesso-negado", tenantSlug)} replace />;
  }

  if (
    requireAuth &&
    tenantSlug &&
    selectedClient?.clientSlug &&
    !isClientSelectionRoute &&
    !isSuperAdmin &&
    normalizeTenantSlug(selectedClient.clientSlug) !== normalizeTenantSlug(tenantSlug)
  ) {
    return <Navigate to={buildTenantPath("/acesso-negado", tenantSlug)} replace />;
  }

  // Check role-based access if roles are specified
  if (allowedRoles) {
    const currentRole = selectedClient?.userRole || user?.userGlobalRole;
    if (!currentRole || !allowedRoles.includes(currentRole)) {
      return <Navigate to={buildTenantPath("/acesso-negado", tenantSlug)} replace />;
    }
  }

  return <>{children}</>;
};

// This function is now centralized in AuthContext.tsx

export default AuthGuard;
